import {
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import {
  FILES,
  isImmune,
  parseSquare,
  pawnImmuneNow,
  sameSquare,
  squareName,
  type MatchEvent,
  type MatchView,
  type Side,
  type Square,
} from '../../engine';
import { t } from '../../i18n';
import { describePawn, itemName, omenName, type Names } from '../../app/describe';
import { TAP_REACH, snapSquare } from '../snapSquare';
import { BoardRoutes } from './BoardRoutes';
import { PawnFigure } from './PawnFigure';
import { OmenRune } from './night/OmenRune';
import { BodyMarks, StatusBadges } from './night/PawnMarks';

export type Highlight =
  'pawn' | 'move' | 'capture' | 'selected' | 'hop' | 'epitaph' | 'collateral' | 'inspect';

/** Highlights a tap or a drop can be aimed at, forgivingly (snapSquare.ts). */
const AIMED: ReadonlySet<Highlight> = new Set([
  'pawn',
  'move',
  'capture',
  'hop',
  'epitaph',
  'collateral',
]);

export interface BoardProps {
  view: MatchView;
  viewer: Side;
  names: Names;
  highlights?: ReadonlyMap<string, Highlight>;
  /** A route to draw: start square, then landings. */
  ghost?: readonly Square[] | null;
  /** A move being previewed, or an Epitaph's route. */
  ghostKind?: 'preview' | 'epitaph';
  /**
   * The route of the latest turn (start square, then landings): its squares are lit, and a trail
   * in the color of `lastMover` follows it, under the pieces and under any preview.
   */
  lastMove?: readonly Square[] | null;
  lastMover?: Side | null;
  marked?: ReadonlySet<string>;
  lastEvents?: readonly MatchEvent[];
  eventsKey?: number;
  /**
   * `lastEvents` are of a drafted step taken back, and play backwards: a pawn goes back through
   * its Looking Glass, a captured pawn fades back in, a Brazier flares as its card comes out.
   */
  undone?: boolean;
  onSquare?: (sq: Square) => void;
  /** Squares (sqKey) whose pawn can be dragged: a finger on them drags instead of scrolling. */
  draggable?: ReadonlySet<string>;
  /** The pawn being dragged: dimmed where it stands. */
  lifted?: string | null;
  /** The pawn chosen to pay for a Wild, not yet played: marked with a pawn ticket. */
  pawning?: string | null;
  /**
   * A boss's power being shown (night/Horrors.tsx): pawns held where they stood until the hands
   * reach them, pawns lifted off the board, the classes that turn or cut the board, the half in
   * the dark (Blind Eye), and what is drawn over the squares and over the whole board.
   */
  heldAt?: Readonly<Record<string, Square>> | null;
  lifting?: readonly string[];
  stage?: string;
  dark?: readonly number[] | null;
  inner?: ReactNode;
  outer?: ReactNode;
  /** Pointer hover (mouse or pen; touch has no hover). `moved` is false for hover changes caused by scrolling. */
  onHover?: (sq: Square | null, moved: boolean) => void;
  /** Keyboard focus within the board. */
  onFocusSquare?: (sq: Square | null) => void;
  reduceMotion: boolean;
  label: string;
  /** Hung on the board's frame (the Long Night's House Rule placard). */
  frame?: ReactNode;
  /** Closing Time: his side fills with your former pawns, one at a time (§11.17). */
  arriving?: boolean;
  /** The Long Night: each pawn's seams of gold (homecomings) and Hallmark (§12.10). */
  marks?: Readonly<Record<string, { readonly seams: number; readonly hallmark: boolean }>>;
}

export const sqKey = (s: Square): string => `${s.x},${s.y}`;

/** Display position of a square: the viewer's home row at the bottom (§19.1). */
function toDisplay(sq: Square, viewer: Side): { col: number; row: number } {
  return viewer === 'S' ? { col: sq.x, row: 5 - sq.y } : { col: 5 - sq.x, row: sq.y };
}
function fromDisplay(col: number, row: number, viewer: Side): Square {
  return viewer === 'S' ? { x: col, y: 5 - row } : { x: 5 - col, y: row };
}

export function Board({
  view: v,
  viewer,
  names,
  highlights,
  ghost,
  ghostKind = 'preview',
  lastMove,
  lastMover,
  marked,
  lastEvents,
  eventsKey,
  undone = false,
  onSquare,
  draggable,
  lifted,
  pawning = null,
  heldAt = null,
  lifting,
  stage = '',
  dark = null,
  inner,
  outer,
  onHover,
  onFocusSquare,
  reduceMotion,
  label,
  frame,
  arriving = false,
  marks,
}: BoardProps) {
  const [focus, setFocus] = useState<{ col: number; row: number }>({ col: 0, row: 5 });
  const gridRef = useRef<HTMLDivElement>(null);
  const occupant = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of Object.values(v.pawns))
      if (p.status === 'onBoard' && p.square) m.set(sqKey(p.square), p.id);
    return m;
  }, [v.pawns]);
  const lastSet = useMemo(() => new Set((lastMove ?? []).map(sqKey)), [lastMove]);

  // Captures of the latest action fade out where they happened (a CSS animation per action); a
  // capture taken back fades the pawn back in.
  const captured = (lastEvents ?? []).flatMap((e) =>
    e.type === 'PawnCaptured' ? [{ id: e.pawnId, side: e.side, square: e.square }] : [],
  );
  const fading = undone ? [] : captured;
  const returning = new Set(undone && !reduceMotion ? captured.map((c) => c.id) : []);
  // Particle effects of the latest action (CC0 sprites, see CREDITS.md); none with reduced motion.
  const effects = reduceMotion ? [] : boardEffects(lastEvents ?? [], undone);
  // Pawns that went through a Looking Glass this step: where their link began, the Glass they
  // went into and the one they came out of. A ghost slides into the first and sinks; the pawn
  // rises out of the other. Taken back, it all runs the other way.
  const warps = useMemo(() => {
    const out = new Map<string, { start: Square; into: Square; out: Square }>();
    if (reduceMotion) return out;
    const events = lastEvents ?? [];
    events.forEach((e, i) => {
      if (e.type !== 'PawnMoved' || e.cause !== 'glass' || !e.from) return;
      const played = events
        .slice(0, i)
        .reverse()
        .find((x) => x.type === 'LinkPlayed' && x.pawnId === e.pawnId);
      const start = played?.type === 'LinkPlayed' ? played.step.from : e.from;
      out.set(e.pawnId, { start, into: e.from, out: e.to });
    });
    return out;
  }, [lastEvents, reduceMotion]);
  // Alternates with each step, so that the same animation plays again on the next.
  const alt = (eventsKey ?? 0) % 2 ? 'a' : 'b';
  // The Looking Glasses in play, in their pairs: a faint thread of light joins each pair (drawn
  // once, from the one on the left half). A Shrouded one is not known to be a Glass yet.
  const live = (v.ledger?.omens ?? []).filter((o) => o.kind === 'looking-glass' && !o.spent);
  const glasses = live.filter(
    (o) =>
      o.square.x < 3 &&
      live.some((t) => t.square.x === 5 - o.square.x && t.square.y === 5 - o.square.y),
  );

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const d: Record<string, [number, number]> = {
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
    };
    const step = d[e.key];
    if (step) {
      e.preventDefault();
      const col = Math.min(5, Math.max(0, focus.col + step[0]));
      const row = Math.min(5, Math.max(0, focus.row + step[1]));
      setFocus({ col, row });
      const btn = gridRef.current?.querySelector<HTMLButtonElement>(`[data-cell="${col}-${row}"]`);
      btn?.focus();
      onFocusSquare?.(fromDisplay(col, row, viewer));
    }
  };

  // Rows of gridcells (ARIA grid pattern); each row is a line of the CSS grid.
  const rows = [];
  for (let row = 0; row < 6; row++) {
    const cells = [];
    for (let col = 0; col < 6; col++) {
      const sq = fromDisplay(col, row, viewer);
      const key = sqKey(sq);
      const hl = highlights?.get(key);
      const pawnId = occupant.get(key);
      const pawn = pawnId ? v.pawns[pawnId] : undefined;
      const shade = (sq.x + sq.y) % 2 === 0;
      // Blind Eye: a square in the dark says so, with whatever stands on it.
      const unlit = !!dark?.includes(sq.x);
      const desc = pawn
        ? t('square.with', {
            square: squareName(sq),
            pawn: describePawn(v, pawn, names, marked?.has(pawn.id)),
          })
        : t('square.empty', { square: squareName(sq) });
      const hlText = hl ? `. ${hlLabel(hl)}` : '';
      // Long Night: the Omen on the square (a Shrouded one is a dark square to this side).
      const omen = v.ledger?.omens.find((o) => !o.spent && sameSquare(o.square, sq));
      const omenText = omen ? `. ${omenName(omen.kind)}` : '';
      const darkText = unlit ? `. ${t('square.inTheDark')}` : '';
      cells.push(
        <button
          key={key}
          type="button"
          role="gridcell"
          data-cell={`${col}-${row}`}
          data-square={squareName(sq)}
          data-target={hl && AIMED.has(hl) ? '' : undefined}
          data-origin={hl === 'selected' ? '' : undefined}
          className={`sq ${shade ? 'sq-dark' : 'sq-light'} ${hl ? `hl-${hl}` : ''} ${lastSet.has(key) ? 'sq-last' : ''} ${draggable?.has(key) ? 'sq-drag' : ''}`}
          tabIndex={focus.col === col && focus.row === row ? 0 : -1}
          aria-label={desc + omenText + darkText + hlText}
          onClick={(e) => {
            setFocus({ col, row });
            // A tap just beside a lit square, on an empty one that is not lit, means the lit one.
            const near =
              !hl && !pawn && e.detail > 0
                ? snapSquare([{ x: e.clientX, y: e.clientY }], TAP_REACH)
                : null;
            onSquare?.((near && parseSquare(near)) || sq);
          }}
          onPointerEnter={(e) => e.pointerType !== 'touch' && onHover?.(sq, false)}
          onPointerMove={(e) => e.pointerType !== 'touch' && onHover?.(sq, true)}
          onPointerLeave={(e) => e.pointerType !== 'touch' && onHover?.(null, false)}
          onFocus={() => {
            setFocus({ col, row });
            onFocusSquare?.(sq);
          }}
          onBlur={() => onFocusSquare?.(null)}
        >
          {col === 0 && (
            <span className="sq-rank" aria-hidden="true">
              {sq.y + 1}
            </span>
          )}
          {row === 5 && (
            <span className="sq-file" aria-hidden="true">
              {FILES[sq.x]}
            </span>
          )}
          {omen && (
            <span
              className={`sq-omen omen-${omen.kind}`}
              title={`${omenName(omen.kind)}: ${itemName(`omen.${omen.kind}.text`)}`}
              aria-hidden="true"
            >
              <OmenRune kind={omen.kind} uses={omen.uses} open={occupant.has(key)} />
            </span>
          )}
          {hl && <span className="sq-mark" aria-hidden="true" />}
        </button>,
      );
    }
    rows.push(
      <div key={row} role="row" className="board-row">
        {cells}
      </div>,
    );
  }

  const pawns = Object.values(v.pawns).filter((p) => p.status === 'onBoard' && p.square);
  const flip = viewer === 'N';

  // Blind Eye: the dark half, drawn over the files it covers as the viewer sees them.
  const darkLeft = dark ? toDisplay({ x: Math.min(...dark), y: 0 }, viewer).col : 0;
  const darkCol = dark
    ? Math.min(darkLeft, toDisplay({ x: Math.max(...dark), y: 0 }, viewer).col)
    : 0;

  return (
    <div
      className={`board-wrap ${reduceMotion ? 'reduce-motion' : ''} ${arriving ? 'is-arriving' : ''} ${stage}`}
    >
      <div className="board" role="grid" aria-label={label} ref={gridRef} onKeyDown={onKey}>
        {rows}
        {dark && (
          <span
            className="board-dark"
            style={{ left: `${(darkCol * 100) / 6}%` } as CSSProperties}
            aria-hidden="true"
          />
        )}
        {inner && (
          <div className="horror-inner" aria-hidden="true">
            {inner}
          </div>
        )}
        {glasses.length > 0 && (
          <svg className="glass-threads" viewBox="0 0 6 6" aria-hidden="true">
            {glasses.map((o) => {
              const a = toDisplay(o.square, viewer);
              const b = toDisplay({ x: 5 - o.square.x, y: 5 - o.square.y }, viewer);
              return (
                <line
                  key={o.id}
                  x1={a.col + 0.5}
                  y1={a.row + 0.5}
                  x2={b.col + 0.5}
                  y2={b.row + 0.5}
                />
              );
            })}
          </svg>
        )}
        <BoardRoutes
          viewer={viewer}
          trail={lastMove}
          trailSide={lastMover ?? 'S'}
          ghost={ghost}
          ghostKind={ghostKind}
          occupied={(sq) => occupant.has(sqKey(sq))}
          reduceMotion={reduceMotion}
        />
        <div className="pawn-layer" aria-hidden="true">
          {pawns.map((p) => {
            const d = toDisplay(heldAt?.[p.id] ?? (p.square as Square), viewer);
            const sel = highlights?.get(sqKey(p.square as Square));
            const warp = warps.get(p.id);
            const back = warp && undone ? toDisplay(warp.into, viewer) : null;
            return (
              <div
                key={p.id}
                data-pawn={p.id}
                className={`pawn-slot side-${p.side} ${p.former ? 'is-former' : ''} ${
                  p.standIn ? 'is-stand-in' : ''
                } ${(v.ledger?.statuses[p.id] ?? []).map((st) => `st-${st.kind}`).join(' ')} ${
                  sel === 'selected' ? 'pawn-selected' : ''
                } ${lifted === p.id ? 'pawn-lifted' : ''} ${pawning === p.id ? 'pawn-pawning' : ''} ${
                  lifting?.includes(p.id) ? 'pawn-carried' : ''
                } ${dark && p.square && dark.includes(p.square.x) ? 'pawn-unseen' : ''} ${
                  warp ? `pawn-${undone ? 'unwarp' : 'warp'}-${alt}` : ''
                } ${returning.has(p.id) ? `pawn-return-${alt}` : ''}`}
                style={
                  {
                    transform: `translate(${d.col * 100}%, ${d.row * 100}%)`,
                    '--arrive': d.col,
                    '--c': d.col,
                    '--r': d.row,
                    ...(back ? { '--c0': back.col, '--r0': back.row } : {}),
                  } as CSSProperties
                }
              >
                <PawnFigure
                  side={p.side}
                  crowned={p.crowned}
                  immune={v.ledger ? pawnImmuneNow(v, p) : isImmune(v, p)}
                  inscription={p.inscription}
                  marked={marked?.has(p.id)}
                  flipRoute={flip}
                >
                  <BodyMarks
                    side={p.side}
                    carvings={p.carvings}
                    seams={marks?.[p.id]?.seams}
                    hallmark={marks?.[p.id]?.hallmark}
                    gilded={v.ledger?.statuses[p.id]?.some((st) => st.kind === 'gilded')}
                  />
                </PawnFigure>
                {p.name && <span className="pawn-name">{p.name.split(' ')[0]}</span>}
                <StatusBadges statuses={v.ledger?.statuses[p.id]} />
                {pawning === p.id && <PawnTicket />}
              </div>
            );
          })}
          {effects.map((fx, i) => {
            const d = toDisplay(fx.square, viewer);
            return (
              <div
                key={`fx-${eventsKey ?? 0}-${i}`}
                className={`pawn-slot fx-slot ${undone ? 'is-undone' : ''}`}
                style={{ transform: `translate(${d.col * 100}%, ${d.row * 100}%)` }}
              >
                <span className={`fx fx-${fx.kind}`} />
                {fx.kind !== 'land' && <span className={`fx fx-${fx.kind}-2`} />}
              </div>
            );
          })}
          {[...warps].map(([id, w]) => {
            const p = v.pawns[id];
            if (!p) return null;
            // Forward, the ghost slides from where the link began into the Glass; taken back, it
            // sinks into the Glass the pawn had come out of.
            const a = toDisplay(undone ? w.out : w.start, viewer);
            const b = toDisplay(undone ? w.out : w.into, viewer);
            return (
              <div
                key={`warp-${eventsKey ?? 0}-${id}`}
                className={`pawn-slot warp-ghost ${undone ? 'is-undone' : ''}`}
                style={
                  { '--c0': a.col, '--r0': a.row, '--c1': b.col, '--r1': b.row } as CSSProperties
                }
              >
                <PawnFigure side={p.side} crowned={p.crowned} immune={false} inscription={null} />
              </div>
            );
          })}
          {fading.map((f) => {
            const d = toDisplay(f.square, viewer);
            return (
              <div
                key={`fade-${eventsKey ?? 0}-${f.id}`}
                className="pawn-slot pawn-fading"
                style={{ transform: `translate(${d.col * 100}%, ${d.row * 100}%)` }}
              >
                <PawnFigure side={f.side} crowned={false} immune={false} inscription={null} />
              </div>
            );
          })}
        </div>
      </div>
      {frame}
      {outer && (
        <div className="horror-outer" aria-hidden="true">
          {outer}
        </div>
      )}
    </div>
  );
}

/**
 * A pawn ticket, tied to the pawn chosen to pay for a Wild: a tag of paper in whole pixels with
 * its string and a red seal. 11 × 9.
 */
function PawnTicket() {
  return (
    <span className="pawn-ticket" aria-hidden="true">
      <svg viewBox="0 0 11 9" shapeRendering="crispEdges">
        <path d="M3 1h7v7h-7v-1h-1v-1h-1v-3h1v-1h1z" fill="#2a1d12" />
        <path d="M3 2h6v5h-6v-1h-1v-3h1z" fill="#f1e4c4" />
        <path d="M4 6h5v1h-5z" fill="#c9b48a" />
        <path d="M6 3h2v2h-2z" fill="#c8323a" />
        <path d="M3 4h1v1h-1z" fill="#2a1d12" />
        <path d="M0 4h2v1h-2z" fill="#b8863c" />
      </svg>
    </span>
  );
}

type EffectKind = 'land' | 'capture' | 'crown' | 'pawned' | 'epitaph' | 'glass' | 'burn';

/**
 * Where the latest action's effects play: landings, captures, crownings, pawnings, Epitaphs. A
 * step taken back plays only its Looking Glass swirls and its Brazier's flare.
 */
export function boardEffects(
  events: readonly MatchEvent[],
  undone = false,
): { kind: EffectKind; square: Square }[] {
  const out: { kind: EffectKind; square: Square }[] = [];
  for (const e of events) {
    if (undone && e.type !== 'PawnMoved' && e.type !== 'CardBurned') continue;
    if (e.type === 'LinkPlayed') {
      const last = e.step.landings[e.step.landings.length - 1];
      if (last) out.push({ kind: 'land', square: last });
    } else if (e.type === 'PawnCaptured') out.push({ kind: 'capture', square: e.square });
    else if (e.type === 'PawnCrowned') out.push({ kind: 'crown', square: e.square });
    else if (e.type === 'PawnPawned' && e.square) out.push({ kind: 'pawned', square: e.square });
    else if (e.type === 'PawnMoved' && e.cause === 'glass' && e.from) {
      out.push({ kind: 'glass', square: e.from });
      out.push({ kind: 'glass', square: e.to });
    } else if (e.type === 'CardBurned') out.push({ kind: 'burn', square: e.square });
    else if (e.type === 'EpitaphPlayed') {
      const last = e.route[e.route.length - 1]?.landings.at(-1);
      if (last) out.push({ kind: 'epitaph', square: last });
    }
  }
  return out;
}

function hlLabel(h: Highlight): string {
  return h === 'inspect' ? '' : t(`hl.${h}`);
}
